import React from 'react';
import { Quote, Heart, Sparkles, Target } from 'lucide-react';

export const FoundersStory = () => {
  return (
    <section id="founders-story" className="relative py-24 bg-gunmetal-950">
      {/* Background */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/4031011/pexels-photo-4031011.jpeg)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gunmetal-950 via-gunmetal-950/95 to-gunmetal-950"></div>
        <div className="absolute inset-0 opacity-30 noise-texture"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-block mb-4">
            <Quote className="text-accent-red" size={48} />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4">
            <span className="text-platinum animate-chrome-pulse">FOUNDER'S </span>
            <span className="text-accent-red">STATEMENT</span>
          </h2>
          <div className="w-24 h-1 bg-accent-red mx-auto mb-6"></div>
          <p className="text-xl text-steel-300">
            J.F.R. Jr. — The Man Behind the Mission
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-gradient-to-br from-gunmetal-800/70 to-gunmetal-900/70 backdrop-blur-sm rounded-lg border-2 border-steel-700/30 overflow-hidden">
          {/* Author Signature Bar */}
          <div className="bg-accent-red px-8 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-black text-2xl">Jose Francisco Rodriguez Jr.</h3>
                <p className="text-white/90 text-sm">Founder & Creative Director, MINDESIGN ENTERPRISES</p>
              </div>
              <div className="hidden md:flex space-x-2">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Heart className="text-white" size={20} />
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Sparkles className="text-white" size={20} />
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Target className="text-white" size={20} />
                </div>
              </div>
            </div>
          </div>

          {/* Statement Content */}
          <div className="p-8 md:p-12 space-y-6 text-steel-300 text-lg leading-relaxed">
            <p className="text-xl md:text-2xl font-bold text-steel-100 border-l-4 border-accent-red pl-6 py-2">
              My journey to founding MINDESIGN ENTERPRISES was not paved in a boardroom; it was forged through the fire of lived experience.
            </p>

            <p>
              I am a writer, a creator, and a survivor. My life's work—spanning a vast library of intellectual property, from screenplays to original music—is the byproduct of a profound personal transformation. Having navigated the complexities of the human condition, from the shadows of domestic challenges to the triumphs of recovery, I realized that the world doesn't just need more "content"; <strong className="text-steel-200">it needs a roadmap for restoration.</strong>
            </p>

            <div className="bg-gunmetal-950/80 rounded-lg p-6 border-l-4 border-accent-red">
              <p className="text-2xl font-black text-accent-red mb-2">
                MINDESIGN ENTERPRISES is that roadmap.
              </p>
            </div>

            <p>
              I am uniquely qualified to lead this venture because I speak the language of my target audience fluently. When I write about recovery, homelessness, or the resilience required to overcome addiction, <strong className="text-steel-200">I am not speculating—I am documenting.</strong> This authenticity is the "secret sauce" of the MINDESIGN brand.
            </p>

            <p className="text-xl font-semibold text-steel-200 italic">
              It is why our campaign—<span className="text-accent-red">"Live more, love more, laugh more, give more"</span>—is more than a slogan; it is a lifestyle that I have lived and a promise I am making to you.
            </p>

            <p>
              My background as a prolific creator of intellectual property provides the engine for this enterprise. I have spent years developing a catalog of TV series ideas, screenplays, and music that are ready for the global stage. My dual role as an artist and an advocate allows me to bridge the gap between commercial multimedia and social philanthropy.
            </p>

            <div className="bg-gradient-to-r from-accent-red/10 to-transparent rounded-lg p-6">
              <p className="text-xl font-bold text-steel-100 mb-4">
                I am leading MINDESIGN because I believe that the most profitable businesses of the future are those that solve the deepest human problems.
              </p>
              <p className="text-steel-300">
                I have the vision to see the content, the talent to create it, and the resilience to scale this company into a global force for good.
              </p>
            </div>

            {/* Signature */}
            <div className="pt-8 text-center">
              <p className="text-steel-400 text-lg mb-2">In my most sincere effort!</p>
              <div className="text-4xl font-bold text-accent-red mb-2" style={{ fontFamily: 'Brush Script MT, cursive' }}>
                Jfrjr
              </div>
              <div className="w-32 h-1 bg-accent-red mx-auto"></div>
            </div>
          </div>

          {/* Bottom Banner */}
          <div className="bg-gunmetal-950/90 backdrop-blur-sm rounded-lg p-6 border-t-2 border-accent-red/30">
            <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8 text-center">
              <div>
                <div className="text-accent-red font-black text-lg mb-1">WRITER</div>
                <div className="text-steel-500 text-sm">30+ Original Stories</div>
              </div>
              <div className="hidden md:block w-1 h-12 bg-steel-700"></div>
              <div>
                <div className="text-accent-red font-black text-lg mb-1">CREATOR</div>
                <div className="text-steel-500 text-sm">150+ Songs & Soundscapes</div>
              </div>
              <div className="hidden md:block w-1 h-12 bg-steel-700"></div>
              <div>
                <div className="text-accent-red font-black text-lg mb-1">30 YEARS</div>
                <div className="text-steel-500 text-sm">Building the MINDESIGN VAULT</div>
              </div>
            </div>
          </div>
        </div>

        {/* Closing Quote */}
        <div className="mt-12 text-center">
          <blockquote className="text-2xl md:text-3xl font-black text-diamond italic">
            "This is more than business. This is mission-driven artistry with a soul."
          </blockquote>
          <p className="text-steel-500 mt-4">— Jose Francisco Rodriguez Jr.</p>
        </div>
      </div>
    </section>
  );
};
